import React, { useContext, lazy, Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Container } from 'react-bootstrap'
import Header from './components/Header.js'
import Footer from './components/Footer.js'
import HomeScreen from './screens/HomeScreen.js'
import { ThemeProvider, ThemeContext } from './components/ThemeContext.js'

const AnalyticsTracker = lazy(() => import('./AnalyticsTracker.js'))
const LoginScreen = lazy(() => import('./screens/LoginScreen.js'))
const About = lazy(() => import('./screens/About.js'))
const RegisterScreen = lazy(() => import('./screens/RegisterScreen.js'))
const DashBoard = lazy(() => import('./screens/DashBoard.js'))
const ChartScreen = lazy(() => import('./screens/ChartScreen.js'))
const ReportsScreen = lazy(() => import('./screens/ReportsScreen.js'))
const ReceiptScreen = lazy(() => import('./screens/ReceiptScreen.js'))
const ProfileScreen = lazy(() => import('./screens/ProfileScreen.js'))
const SearchBox = lazy(() => import('./components/SearchBox.js'))
const ReceiptUpdateScreen = lazy(
  () => import('./screens/ReceiptUpdateScreen.js')
)
const CatReceiptScreen = lazy(() => import('./screens/CatReceiptScreen.js'))
const UserListScreen = lazy(() => import('./screens/UserListScreen.js'))
const UserEditScreen = lazy(() => import('./screens/UserEditScreen.js'))
const BusinessRegisterScreen = lazy(
  () => import('./screens/BusinessRegister.js')
)
const OwnerLoginScreen = lazy(() => import('./screens/OwnerLoginScreen.js'))
const OwnerDashBoard = lazy(() => import('./screens/OwnerDashboard.js'))
const OwnerAnalytics = lazy(() => import('./screens/OwnerAnalytics.js'))
const OwnerProfileScreen = lazy(() => import('./screens/OwnerProfileScreen.js'))
const ApiDocument = lazy(() => import('./screens/ApiDocument.js'))
const ReceiptChartScreen = lazy(() => import('./screens/ReceiptChartScreen.js'))
const PremiumScreen = lazy(() => import('./screens/PremiumScreen.js'))

const HomeRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  return <HomeScreen />
}

const LoginRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  return <LoginScreen />
}

const RegisterRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  return <RegisterScreen />
}

const OwnerLoginRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  return <OwnerLoginScreen />
}

const BusinessRegisterRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  return <BusinessRegisterScreen />
}

const AdminRegisterRoute = () => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (userInfo?.isAdmin) {
    return <RegisterScreen />
  }

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  return <Navigate to='/login' replace />
}

const RequireUserRoute = ({ children }) => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  if (!userInfo) {
    return <Navigate to='/login' replace />
  }

  return children
}

const RequireOwnerRoute = ({ children }) => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (userInfo) {
    return <Navigate to='/dashboard' replace />
  }

  if (!ownerInfo) {
    return <Navigate to='/ownerlogin' replace />
  }

  return children
}

const RequireAdminRoute = ({ children }) => {
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  if (ownerInfo) {
    return <Navigate to='/ownerdashboard' replace />
  }

  if (!userInfo) {
    return <Navigate to='/login' replace />
  }

  if (!userInfo.isAdmin) {
    return <Navigate to='/dashboard' replace />
  }

  return children
}

const AppRoutes = () => {
  const { theme } = useContext(ThemeContext)

  return (
    <div
      style={{
        backgroundColor: theme.bg,
        color: theme.text,
        minHeight: '100vh'
      }}
    >
      <main className={theme}>
        <Container className='py-3'>
          <Header />
          <Suspense
            fallback={<div style={{ padding: '1rem 0' }}>Loading...</div>}
          >
            <Routes>
              <Route path='/' element={<HomeRoute />} />
              <Route path='/about' element={<About />} />
              <Route path='/login' element={<LoginRoute />} />
              <Route path='/ownerlogin' element={<OwnerLoginRoute />} />
              <Route path='/register' element={<RegisterRoute />} />
              <Route
                path='/admin/user/register'
                element={<AdminRegisterRoute />}
              />
              <Route
                path='/businessregister'
                element={<BusinessRegisterRoute />}
              />
              <Route
                path='/receipts'
                element={
                  <RequireUserRoute>
                    <ReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/category'
                element={
                  <RequireUserRoute>
                    <CatReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/receipts/:id'
                element={
                  <RequireUserRoute>
                    <ReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/receipts/edit/:id'
                element={
                  <RequireUserRoute>
                    <ReceiptUpdateScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/dashboard'
                element={
                  <RequireUserRoute>
                    <DashBoard />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/ownerdashboard'
                element={
                  <RequireOwnerRoute>
                    <OwnerDashBoard />
                  </RequireOwnerRoute>
                }
              />
              <Route
                path='/owneranalytics'
                element={
                  <RequireOwnerRoute>
                    <OwnerAnalytics />
                  </RequireOwnerRoute>
                }
              />
              <Route
                path='/charts'
                element={
                  <RequireUserRoute>
                    <ChartScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/reports'
                element={
                  <RequireUserRoute>
                    <ReportsScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/profile'
                element={
                  <RequireUserRoute>
                    <ProfileScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/ownerprofile'
                element={
                  <RequireOwnerRoute>
                    <OwnerProfileScreen />
                  </RequireOwnerRoute>
                }
              />
              <Route
                path='/search/:keyword'
                element={
                  <RequireUserRoute>
                    <ReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/page/:pageNumber'
                element={
                  <RequireUserRoute>
                    <ReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/catpage/:catpageNumber'
                element={
                  <RequireUserRoute>
                    <CatReceiptScreen />
                  </RequireUserRoute>
                }
              />
              <Route
                path='/search'
                element={
                  <RequireUserRoute>
                    <SearchBox />
                  </RequireUserRoute>
                }
              />
              <Route path='*' element={<h1>404 Not Found</h1>} />
              <Route
                path='/userlist'
                element={
                  <RequireAdminRoute>
                    <UserListScreen />
                  </RequireAdminRoute>
                }
              />
              <Route
                path='/modifyuser/:id'
                element={
                  <RequireAdminRoute>
                    <UserEditScreen />
                  </RequireAdminRoute>
                }
              />
              <Route
                path='/apidocument'
                element={
                  <RequireOwnerRoute>
                    <ApiDocument />
                  </RequireOwnerRoute>
                }
              />
              <Route
                path='/receiptchart'
                element={
                  <RequireOwnerRoute>
                    <ReceiptChartScreen />
                  </RequireOwnerRoute>
                }
              />
              <Route
                path='/premium'
                element={
                  <RequireUserRoute>
                    <PremiumScreen />
                  </RequireUserRoute>
                }
              />
            </Routes>
          </Suspense>
          <Footer />
        </Container>
      </main>
    </div>
  )
}

const App = () => {
  const [enableAnalytics, setEnableAnalytics] = useState(false)

  useEffect(() => {
    let timeoutId
    let idleId

    const mountAnalytics = () => setEnableAnalytics(true)

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(mountAnalytics, { timeout: 2000 })
    } else {
      timeoutId = window.setTimeout(mountAnalytics, 0)
    }

    return () => {
      if (
        typeof window !== 'undefined' &&
        idleId &&
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  return (
    <ThemeProvider>
      {enableAnalytics ? (
        <Suspense fallback={null}>
          <AnalyticsTracker />
        </Suspense>
      ) : null}
      <AppRoutes />
    </ThemeProvider>
  )
}

export default App
